import type { GetSnapshotsKeySettings200PrivilegesItem } from '@ipfabric/api-client/client/schemas';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import type { EnablePassword } from 'components/DeviceCredentials/EnablePasswordsList';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { PrivilegedCredentials } from '../components/PrivilegedCredentials/PrivilegedCredentials';
import type { DiscoverySettingsObject } from '../model/DiscoverySettingsObject';

export type PrivilegedCredentialsFieldValues = {
  privileges: EnablePassword[];
};
export type PrivilegedCredentialsOutputValues = {
  privileges: EnablePassword[];
};

const PrivilegedCredentialsFormAdapter = (props: {
  form: UseFormReturn<
    PrivilegedCredentialsFieldValues,
    unknown,
    PrivilegedCredentialsOutputValues
  >;
}) => {
  const privileges = useWatch({
    control: props.form.control,
    name: 'privileges',
  });
  return (
    <PrivilegedCredentials
      privileges={privileges}
      onPrivilegesChange={(privileges) => {
        props.form.setValue('privileges', privileges, { shouldDirty: true });
      }}
    />
  );
};

export const storedPrivilegedCredentials: FormSnippet<
  DiscoverySettingsObject,
  PrivilegedCredentialsFieldValues,
  PrivilegedCredentialsOutputValues
> = {
  render: ({ form }) => <PrivilegedCredentialsFormAdapter form={form} />,

  fromModel: (model, fieldValuesDraft) => {
    // normalize and copy incoming credentials so they match EnablePassword[]
    fieldValuesDraft.privileges = (model.privileges ?? []).map((c) => ({
      ...c,
      password: c.password ?? undefined,
      includeNetworks: c.includeNetworks ?? [],
      excludeNetworks: c.excludeNetworks ?? [],
      notes: c.notes ?? '',
      expirationDate: {
        enabled: c.expirationDate?.enabled ?? false,
        value: c.expirationDate?.value ?? undefined,
      },
    }));
  },

  toModel: (transformedValues, modelDraft) => {
    // TODO NIM-21979 instead of the type cast, polish EnablePassword type, and probably also
    // backend type: generated GetSnapshotsKeySettings200PrivilegesItem declares `password: string`,
    // but https://gitlab.com/ip-fabric/development/product/ipfabric-product/-/blob/25919d82a721b4d0ddcd214decc233681a7674c2/packages/utils/src/lib/settings/types/types.ts#L16
    // suggests `string | null`.
    modelDraft.privileges =
      transformedValues.privileges as GetSnapshotsKeySettings200PrivilegesItem[];
  },

  schema: z.object({
    privileges: z.any(),
  }) as z.Schema<{ privileges: EnablePassword[] }>,
};
